import React, { FC, ReactNode, useEffect } from 'react'
import { useIntl } from 'react-intl'
import { makeStyles } from 'tss-react/mui'
import {
  Divider,
  Unstable_Grid2 as Grid,
  Link as MUILink,
  Typography,
  useMediaQuery,
} from '@mui/material'
import { useTheme } from '@mui/material/styles'
import { useShoppingAi } from '@src/routes/ShoppingAi/useShoppingAi'
import messages from '@src/routes/SearchResults/messages'
import SearchTips from '@src/components/SearchResults/SearchTips'
import ProductCategories from '@src/components/ProductCategories'
import { Link } from '@src/components/Link'
import { GeneSearchQuery } from '@src/queries/GeneSearchQuery.generated'
import { useSearchQuery } from '../../pages/[country]/[language]/search/[searchTerm]'
import { Breakpoints } from '@src/utils/useResponsiveSizes'
import { sendSearchedTermEvent } from '@utils/analytics'
import AdditionalHelp from './AdditionalHelp'
import noSearchResultsStyles from './NoSearchResultsStyles'
import Gene from './GeneSearchResults/Gene'
import ProductSearchShoppingAiBanner from './ProductSearchShoppingAiBanner/ProductSearchShoppingAiBanner'

const useStyles = makeStyles()((theme) => ({
  baseCardStyle: {
    backgroundColor: theme.palette.common.white,
    marginBottom: theme.spacing(5),
    minHeight: 350,
    transition: 'all .2s ease-in-out',
    '&:hover': {
      boxShadow:
        '0 20px 40px 0 rgba(0, 0, 0, 0.2), 0 2px 4px 0 rgba(0, 0, 0, 0.05)',
      transform: 'scale(1.03)',
      '& button': { opacity: 1 },
    },
    [theme.breakpoints.down('lg')]: {
      minHeight: 80,
      marginBottom: 0,
      paddingBottom: 0,
      transition: 'none',
      '&:hover': {
        transform: 'none',
        boxShadow: 'none',
      },
    },
  },
  verticalSpacing: {
    marginBottom: theme.spacing(6),
    [theme.breakpoints.up('xs')]: {
      marginLeft: theme.spacing(6),
    },
  },
  noMatchesMessage: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightMedium,
    [theme.breakpoints.up('md')]: {
      fontSize: theme.typography.pxToRem(18),
      fontWeight: theme.typography.fontWeightBold,
    },
  },
  sectionWrapper: {
    overflow: 'hidden',
    margin: theme.spacing(4, 0),
    [theme.breakpoints.up('xs')]: {
      marginLeft: -20,
      marginRight: -20,
      marginTop: 10,
    },
  },
  sectionHeading: {
    fontSize: theme.typography.pxToRem(16),
    fontWeight: theme.typography.fontWeightBold,
    margin: theme.spacing(10, 0, 2, 0),
  },
  sectionText: {
    fontSize: theme.typography.pxToRem(14),
    lineHeight: '1.8rem',
    margin: '0',
  },
  linkWrapper: {
    margin: theme.spacing(2, 0, 2, 2),
    [theme.breakpoints.up('xs')]: {
      marginLeft: 30,
      marginTop: 20,
    },
  },
  textLink: {
    fontSize: theme.typography.pxToRem(14),
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.primary.main,
  },

  sectionDivider: {
    marginBottom: 20,
  },
  divider: {
    [theme.breakpoints.up('md')]: {
      display: 'none',
    },
    [theme.breakpoints.up('xs')]: {
      marginRight: -20,
      marginLeft: -20,
    },
  },
  cardGrid: {
    [theme.breakpoints.up('md')]: {
      padding: '0 !important',
      margin: -16,
    },
  },
}))

const CascadesResults: FC<{
  link?: string
  linkText?: string
  title: string
  type: string
  isGene?: boolean
  children: ReactNode
}> = ({ link, linkText, title, type, isGene, children }) => {
  const { classes } = useStyles()

  return (
    <div
      className={classes.sectionWrapper}
      data-testid={`search-no-results-${type}-quick-link`}
    >
      <Divider className={classes.sectionDivider} />
      <Typography variant="h2" className={classes.verticalSpacing}>
        {title}
      </Typography>
      <Grid container={isGene ? false : true}>{children}</Grid>
      <Divider className={classes.divider} />
      {link && linkText && (
        <Grid container spacing={4}>
          <div className={classes.linkWrapper}>
            <Link href={link} passHref>
              <MUILink>{linkText}</MUILink>
            </Link>
          </div>
        </Grid>
      )}
    </div>
  )
}

interface NoResultsProps {
  geneData?: GeneSearchQuery
  isRedirect?: string | null | undefined
}

const NoResults: FC<NoResultsProps> = ({ geneData, isRedirect }) => {
  const [query] = useSearchQuery()
  const { formatMessage } = useIntl()
  const { classes } = useStyles()
  const theme = useTheme()
  const isProductAssistantEnabled = useShoppingAi()
  const isMdOrUp = useMediaQuery(theme.breakpoints.up(Breakpoints.MD), {
    defaultMatches: true,
  })
  const isLgOrUp = useMediaQuery(theme.breakpoints.up(Breakpoints.LG), {
    defaultMatches: true,
  })

  useEffect(() => {
    if (!isRedirect) {
      sendSearchedTermEvent(`${query.term}`, {
        searchTerm: query.term,
        searchAutoSuggest: undefined,
        searchAutoSuggestTerm: undefined,
        searchType: 'products',
        searchComponent: 'global search',
        searchErrorMessage: undefined,
        searchResultCount: 0,
      })
    }
  }, [query.term])
  const { classes: nsrClasses } = noSearchResultsStyles()
  const hasGeneData =
    !!geneData?.getGeneSearchResults?.metadata?.itemCount &&
    geneData?.getGeneSearchResults?.metadata?.itemCount !== 0
  const genePreviewData = geneData?.getGeneSearchResults?.items?.slice(0, 1)

  return (
    <Grid className={nsrClasses.gridWrapper}>
      <div className={nsrClasses.columnWrapper}>
        <div className={nsrClasses.firstColumn}>
          {query.term && query.term !== 'facet-search' ? (
            <Typography component="div" className={classes.noMatchesMessage}>
              {`${formatMessage(messages.SORRY_NO_MATCHES_FOUND_FOR)} "${
                query.term
              }"`}
            </Typography>
          ) : (
            (!query.term || query.term === 'facet-search') && (
              <Typography component="div" className={classes.noMatchesMessage}>
                {`${formatMessage(messages.SORRY_NO_RETURN_RESPONSE_FOR)}`}
              </Typography>
            )
          )}
          <SearchTips />
          {isProductAssistantEnabled && <ProductSearchShoppingAiBanner />}
          {hasGeneData && genePreviewData && (
            <CascadesResults
              title={formatMessage({ id: 'GENES', defaultMessage: 'Genes' })}
              type={'gene'}
              isGene
            >
              {genePreviewData.map((gene, index: number) => (
                <>
                  <Divider className={classes.divider} />
                  <Gene key={`${gene.name}-${index}`} gene={gene} />
                </>
              ))}
            </CascadesResults>
          )}
          {isMdOrUp && <ProductCategories />}
        </div>
        {isLgOrUp && <AdditionalHelp />}
      </div>
      {!isLgOrUp && <AdditionalHelp />}
    </Grid>
  )
}

export default NoResults
